import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Network, ArrowLeft, Loader2 } from 'lucide-react';

/**
 * DocsGraphPage — visual map of the markdown corpus, derived from
 * `[[wikilink]]` references parsed by backend/commerce_aging/playbook_engine.py.
 *
 * Same data as Obsidian's graph view, rendered inside the dashboard so
 * users don't need Obsidian. Uses plain SVG with a deterministic layout
 * (no external graph library) — every scope (workers/departments/etc.)
 * gets its own column, nodes sized by incoming-link count.
 */
export default function DocsGraphPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hoverNode, setHoverNode] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem('session_token');
        const res = await fetch(
          (process.env.REACT_APP_BACKEND_URL || '') + '/api/playbooks/graph',
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (e) {
        setError(e.message || 'Failed to load graph');
      }
    })();
  }, []);

  // Group nodes by scope and assign x/y positions (deterministic, no physics).
  const layout = useMemo(() => {
    if (!data || !data.nodes) return null;

    const SCOPE_ORDER = ['platform', 'departments', 'workers', 'flows', 'integrations', 'folders', 'phantom'];
    const COLUMNS = SCOPE_ORDER.length;
    const COL_WIDTH = 220;
    const VERT_PADDING = 60;

    // Incoming-link count per node (= node size)
    const incoming = {};
    data.edges.forEach((e) => {
      incoming[e.target] = (incoming[e.target] || 0) + 1;
    });

    // Group nodes by scope
    const byScope = {};
    SCOPE_ORDER.forEach((s) => { byScope[s] = []; });
    data.nodes.forEach((n) => {
      const scope = byScope[n.scope] ? n.scope : 'integrations';
      byScope[scope].push({ ...n, incoming: incoming[n.id] || 0 });
    });

    // Within each column, sort by incoming-count desc so the most-linked nodes are at top
    Object.values(byScope).forEach((arr) => {
      arr.sort((a, b) => b.incoming - a.incoming);
    });

    // Compute positions
    const positioned = {};
    let maxColumnHeight = 0;
    SCOPE_ORDER.forEach((scope, colIdx) => {
      const nodes = byScope[scope];
      const x = colIdx * COL_WIDTH + COL_WIDTH / 2 + 40;
      nodes.forEach((node, rowIdx) => {
        const y = VERT_PADDING + rowIdx * 32 + 30;
        positioned[node.id] = { ...node, x, y, columnIdx: colIdx };
      });
      maxColumnHeight = Math.max(maxColumnHeight, nodes.length * 32 + VERT_PADDING * 2);
    });

    return {
      nodes: positioned,
      edges: data.edges,
      width: COLUMNS * COL_WIDTH + 80,
      height: Math.max(maxColumnHeight, 600),
      scopeOrder: SCOPE_ORDER,
      byScope,
    };
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] text-white p-8">
        <div className="bg-[#0D1628] border border-[#FF6B8A]/40 rounded-xl p-6 max-w-2xl">
          <div className="text-[#FF6B8A] font-semibold mb-2">Could not load the docs graph</div>
          <div className="text-sm text-[#94A3B8]">{error}</div>
          <Link to="/docs" className="text-[#3EF1C7] text-sm hover:underline mt-3 inline-block">
            ← Back to docs
          </Link>
        </div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#3EF1C7]" />
      </div>
    );
  }

  const SCOPE_COLORS = {
    platform: '#FFD166',
    departments: '#7A5CFF',
    workers: '#3EF1C7',
    flows: '#FF6B8A',
    integrations: '#38BDF8',
    folders: '#A78BFA',
    phantom: '#475569',
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to Commerce Index
          </Link>
          <div className="flex items-center gap-2">
            <Network className="w-5 h-5 text-[#3EF1C7]" />
            <span className="font-semibold">Docs Graph</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Documentation Graph</h1>
          <p className="text-[#94A3B8]">
            Every playbook and the wikilinks between them. Same data as Obsidian's graph view —
            derived from <code className="text-[#3EF1C7]">[[wikilinks]]</code> in the markdown corpus
            via <code className="text-[#3EF1C7]">/api/playbooks/graph</code>.
            See <Link to="/docs" className="text-[#3EF1C7] hover:underline">docs/MARKDOWN_CONVENTIONS.md</Link> for the linking rules.
          </p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-6 text-xs">
          {layout.scopeOrder.map((scope) => (
            <div key={scope} className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D1628] border border-[#22304A]">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SCOPE_COLORS[scope] }} />
              <span className="text-[#94A3B8]">
                {scope === 'phantom' ? 'phantom (no playbook)' : scope}
              </span>
              <span className="text-[#475569]">{layout.byScope[scope].length}</span>
            </div>
          ))}
        </div>

        {/* SVG canvas */}
        <div className="bg-[#0D1628] border border-[#22304A] rounded-xl overflow-auto">
          <svg
            width={layout.width}
            height={layout.height}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            style={{ display: 'block' }}
          >
            {/* Column headers */}
            {layout.scopeOrder.map((scope, colIdx) => {
              const x = colIdx * 220 + 110 + 40;
              return (
                <text
                  key={`hdr-${scope}`}
                  x={x}
                  y={20}
                  fill={SCOPE_COLORS[scope]}
                  fontSize="11"
                  fontWeight="600"
                  textAnchor="middle"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  {scope}
                </text>
              );
            })}

            {/* Edges */}
            {layout.edges.map((edge, i) => {
              const src = layout.nodes[edge.source];
              const tgt = layout.nodes[edge.target];
              if (!src || !tgt) return null;
              const isHovered =
                hoverNode &&
                (hoverNode === edge.source || hoverNode === edge.target);
              const stroke = isHovered ? '#3EF1C7' : '#22304A';
              const strokeOpacity = isHovered ? 0.9 : 0.35;
              const strokeWidth = isHovered ? 1.5 : 0.8;
              const dx = (tgt.x - src.x) * 0.35;
              const path = `M ${src.x} ${src.y} C ${src.x + dx} ${src.y}, ${tgt.x - dx} ${tgt.y}, ${tgt.x} ${tgt.y}`;
              return (
                <path
                  key={`edge-${i}`}
                  d={path}
                  fill="none"
                  stroke={stroke}
                  strokeOpacity={strokeOpacity}
                  strokeWidth={strokeWidth}
                />
              );
            })}

            {/* Nodes */}
            {Object.values(layout.nodes).map((node) => {
              const r = Math.min(10, 3 + node.incoming);
              const color = SCOPE_COLORS[node.scope] || '#94A3B8';
              const isHovered = hoverNode === node.id;
              return (
                <g
                  key={node.id}
                  onMouseEnter={() => setHoverNode(node.id)}
                  onMouseLeave={() => setHoverNode(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={r}
                    fill={node.exists === false ? '#0D1628' : color}
                    stroke={color}
                    strokeWidth={node.exists === false ? 1.5 : 0}
                    strokeDasharray={node.exists === false ? '3,2' : '0'}
                    opacity={isHovered ? 1 : 0.85}
                  />
                  <text
                    x={node.x + r + 4}
                    y={node.y + 3}
                    fill={isHovered ? '#ffffff' : '#94A3B8'}
                    fontSize="10"
                    fontFamily="ui-monospace, monospace"
                  >
                    {node.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Detail panel for hovered node */}
        {hoverNode && layout.nodes[hoverNode] && (
          <div className="mt-6 bg-[#0D1628] border border-[#22304A] rounded-xl p-6">
            <div className="text-xs uppercase tracking-wider text-[#64748B]">
              {layout.nodes[hoverNode].scope}
            </div>
            <div className="text-xl font-semibold mt-1">
              {layout.nodes[hoverNode].name}
            </div>
            {layout.nodes[hoverNode].objective && (
              <div className="text-sm text-[#94A3B8] mt-2 max-w-3xl">
                {layout.nodes[hoverNode].objective}
              </div>
            )}
            <div className="text-xs text-[#64748B] mt-3">
              Incoming wikilinks: <span className="text-[#3EF1C7]">{layout.nodes[hoverNode].incoming}</span>
              {layout.nodes[hoverNode].reports_to && (
                <span className="ml-4">
                  Reports to: <span className="text-[#94A3B8]">{layout.nodes[hoverNode].reports_to}</span>
                </span>
              )}
              {layout.nodes[hoverNode].exists === false && (
                <span className="ml-4 text-[#FFD166]">
                  Phantom — no playbook file yet
                </span>
              )}
            </div>
          </div>
        )}

        {/* Footer / explanation */}
        <div className="mt-6 text-xs text-[#64748B]">
          Hover any node to highlight its connections. Dashed circles are
          "phantom" targets — wikilinks pointing at a playbook that doesn't
          exist yet (useful as a TODO list for the playbook backfill).
        </div>
      </div>
    </div>
  );
}
